import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { getCoupons, updateCoupon } from '../../services/couponApi';
import { getDoctorAvailabilityForecast } from '../../services/doctorAvailabilityApi';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Phone, Mail, Calendar, Clock, UserPlus, Stethoscope,
  Loader2, CheckCircle, ChevronRight, ChevronLeft, Search, User, CreditCard, Tag,
  Sparkles, ShieldCheck, ClipboardCheck, BadgeCheck, RefreshCw, FileText, XCircle, X, Upload, Eye, Image, Download,
  Activity, Heart, AlertCircle, Trash2, Plus, Wallet, Shield, FileCheck, CheckCircle2, File, Paperclip
} from 'lucide-react';


const STEPS = [
  { key: 'lead', label: 'Select Lead', icon: <User size={18} /> },
  { key: 'clinical', label: 'Clinical Info', icon: <FileText size={18} /> },
  { key: 'doctor_type', label: 'Doctor Type', icon: <Stethoscope size={18} /> },
  { key: 'schedule', label: 'Schedule', icon: <Calendar size={18} /> },
  { key: 'payment', label: 'Payment', icon: <CreditCard size={18} /> },
  { key: 'confirm', label: 'Confirm', icon: <CheckCircle size={18} /> }
];

const TIME_SLOTS = [
  { value: '09:00-10:00', label: '09:00 AM - 10:00 AM' },
  { value: '10:00-11:00', label: '10:00 AM - 11:00 AM' },
  { value: '11:00-12:00', label: '11:00 AM - 12:00 PM' },
  { value: '12:00-13:00', label: '12:00 PM - 01:00 PM' },
  { value: '14:00-15:00', label: '02:00 PM - 03:00 PM' },
  { value: '15:00-16:00', label: '03:00 PM - 04:00 PM' },
  { value: '16:00-17:00', label: '04:00 PM - 05:00 PM' },
  { value: '17:00-18:00', label: '05:00 PM - 06:00 PM' },
];

const formatTime12Hour = (time24) => {
  if (!time24 || typeof time24 !== 'string') return '';
  if (time24.includes('AM') || time24.includes('PM')) return time24;
  const [hoursStr, minutesStr] = time24.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

const getTimeParts = (timeStr) => {
  if (!timeStr) return { hour24: 10, minute: '00', hour12: 10, period: 'AM', raw24: '10:00', display12: '10:00 AM' };
  let h = 10;
  let m = 0;
  let period = 'AM';

  if (typeof timeStr === 'string') {
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const isPM = /pm/i.test(timeStr);
      const clean = timeStr.replace(/AM|PM/i, '').trim();
      const [hStr, mStr] = clean.split(':');
      let parsedH = parseInt(hStr, 10) || 12;
      m = parseInt(mStr, 10) || 0;
      period = isPM ? 'PM' : 'AM';
      if (isPM && parsedH < 12) h = parsedH + 12;
      else if (!isPM && parsedH === 12) h = 0;
      else h = parsedH;
    } else if (timeStr.includes(':')) {
      const [hStr, mStr] = timeStr.split(':');
      h = parseInt(hStr, 10) || 0;
      m = parseInt(mStr, 10) || 0;
      period = h >= 12 ? 'PM' : 'AM';
    }
  }

  const hour12 = h % 12 || 12;
  const minuteStr = String(m).padStart(2, '0');
  const hour24Str = String(h).padStart(2, '0');

  return {
    hour24: h,
    minute: minuteStr,
    hour12,
    period,
    raw24: `${hour24Str}:${minuteStr}`,
    display12: `${String(hour12).padStart(2, '0')}:${minuteStr} ${period}`
  };
};

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr === 'string') {
    const trimmed = dateStr.trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const parseDateInput = (value) => {
  if (!value) return '';
  const [day, month, year] = value.split('-');
  if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
    return `${year}-${month}-${day}`;
  }
  return '';
};

const formatWithHyphens = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
};

const APPOINTMENT_TYPES = [
  { value: 'consultation', label: 'Online Consultation', desc: 'General medical consultation' },
  { value: 'pre_surgery_consult', label: 'Pre-Surgery Consult', desc: 'Review before surgical procedure' },
  { value: 'follow_up', label: 'Follow-Up', desc: 'Post-treatment follow-up visit' },
];

const APPOINTMENT_TYPE_COLORS = {
  consultation: 'bg-blue-50 text-blue-700 border-blue-200',
  pre_surgery_consult: 'bg-amber-50 text-amber-700 border-amber-200',
  follow_up: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const COMMUNICATION_MODES = [
  { value: 'in_person', label: 'In Person', desc: 'Visit clinic/hospital' },
  { value: 'video_call', label: 'Video Call', desc: 'Online video consultation' },
  { value: 'phone_call', label: 'Phone Call', desc: 'Telephonic consultation' },
];

const COMMUNICATION_MODE_COLORS = {
  in_person: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  video_call: 'bg-purple-50 text-purple-700 border-purple-200',
  phone_call: 'bg-slate-50 text-slate-700 border-slate-200',
};

const AppointmentCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchingOptions, setFetchingOptions] = useState(true);
  const [leads, setLeads] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [leadSearch, setLeadSearch] = useState('');
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  const [paymentLink, setPaymentLink] = useState('');
  const [generatingPayment, setGeneratingPayment] = useState(false);
  const [doctorType, setDoctorType] = useState('in_house');
  const [coupons, setCoupons] = useState([]);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [preselectedLead, setPreselectedLead] = useState(null);
  const [doctorConsultationFee, setDoctorConsultationFee] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [appointmentExistsError, setAppointmentExistsError] = useState('');
  const [availabilityDate, setAvailabilityDate] = useState(new Date().toISOString().split('T')[0]);
  const [availabilityDoctorType, setAvailabilityDoctorType] = useState('');
  const [forecast, setForecast] = useState([]);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [submittingLost, setSubmittingLost] = useState(false);
  const [clinicalInfo, setClinicalInfo] = useState({
    chiefComplaints: '',
    pastMedicalHistory: '',
    comorbidities: ''
  });
  const [communicationMode, setCommunicationMode] = useState('in_person');
  const getLeadCoverageMode = (lead) => {
    if (!lead) return '';
    if (lead.coverageMode) return lead.coverageMode;
    if (lead.hasAyushmanCard) return 'ayushman';
    if (lead.paymentType?.includes('Insurance')) return 'insurance';
    if (lead.paymentType?.includes('Cash')) return 'cash';
    return '';
  };

  const [coverageMode, setCoverageMode] = useState('');
  const [idProof, setIdProof] = useState(null);
  const [ayushmanCard, setAyushmanCard] = useState(null);
  const [medicalReports, setMedicalReports] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [isDraggingReports, setIsDraggingReports] = useState(false);
  const [formData, setFormData] = useState({
    leadId: '',
    patientName: '',
    patientPhone: '',
    patientEmail: '',
    appointmentType: 'pre_surgery_consult',
    communicationMode: 'in_person',
    appointmentDate: '',
    appointmentTimeSlot: '',
    appointmentDoctorId: '',
    notes: '',
    status: 'draft'
  });
  const [pricing, setPricing] = useState({
    doctorFee: 0,
    platformFee: 0,
    otherCharges: 0,
    discountPercentage: 0,
    finalPrice: 1000,
    subtotal: 0,
    editing: false,
    taxType: 'cgst_sgst',
    taxPercentage: 18,
    taxName: '',
    netAmount: 0
  });
  const [couponOriginal, setCouponOriginal] = useState(null);
  const STEP_QUERY_KEY = 'step';
  const STORAGE_KEY = 'appointment_create_draft';
  const isInitializedRef = useRef(false);

  const getLeadStorageKey = (id) => id ? `doxez_appointment_draft_${id}` : STORAGE_KEY;

  const clearDraft = (targetLeadId) => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      const activeLeadId = targetLeadId || formData.leadId || preselectedLead?._id;
      if (activeLeadId) {
        localStorage.removeItem(`doxez_appointment_draft_${activeLeadId}`);
        localStorage.removeItem(`doxez_lead_last_step_${activeLeadId}`);
      }
    } catch (e) {
      // ignore storage errors
    }
  };

  const scheduleDateRef = useRef(null);
  const availabilityDateRef = useRef(null);

  const updateUrlStep = (stepIndex) => {
    const stepKey = visibleSteps[stepIndex]?.key;
    if (!stepKey) return;
    const url = new URL(window.location.href);
    url.searchParams.set(STEP_QUERY_KEY, stepKey);
    const activeLeadId = formData.leadId || preselectedLead?._id || searchParams.get('leadId') || searchParams.get('fromLeadId');
    if (activeLeadId && !url.searchParams.get('leadId') && !url.searchParams.get('fromLeadId')) {
      url.searchParams.set('leadId', activeLeadId);
    }
    window.history.replaceState({}, '', url.toString());
  };

  const persistDraft = () => {
    if (!isInitializedRef.current) return;
    try {
      const activeLeadId = formData.leadId || preselectedLead?._id || searchParams.get('leadId') || searchParams.get('fromLeadId');
      const safeFormData = { ...formData };
      if (safeFormData.leadId && typeof safeFormData.leadId !== 'string') {
        safeFormData.leadId = String(safeFormData.leadId);
      }
      const currentStepKey = visibleSteps[currentStep]?.key || 'lead';
      const draft = {
        formData: safeFormData,
        pricing,
        doctorType,
        selectedCouponId,
        clinicalInfo,
        communicationMode,
        coverageMode,
        currentStep: currentStepKey,
        currentStepIndex: currentStep,
        leadId: activeLeadId || '',
        updatedAt: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      if (activeLeadId) {
        localStorage.setItem(`doxez_appointment_draft_${activeLeadId}`, JSON.stringify(draft));
        localStorage.setItem(`doxez_lead_last_step_${activeLeadId}`, currentStepKey);
      }
    } catch (e) {
      // ignore storage errors
    }
  };

  const isPastDate = (dateStr) => {
    if (!dateStr) return false;
    let y, m, d;
    if (typeof dateStr === 'string') {
      const match = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        y = parseInt(match[1], 10);
        m = parseInt(match[2], 10) - 1;
        d = parseInt(match[3], 10);
      } else {
        const dmy = dateStr.trim().match(/^(\d{2})-(\d{2})-(\d{4})/);
        if (dmy) {
          d = parseInt(dmy[1], 10);
          m = parseInt(dmy[2], 10) - 1;
          y = parseInt(dmy[3], 10);
        }
      }
    }
    if (y === undefined) {
      const dateObj = new Date(dateStr);
      if (isNaN(dateObj.getTime())) return false;
      y = dateObj.getUTCFullYear();
      m = dateObj.getUTCMonth();
      d = dateObj.getUTCDate();
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfTarget = new Date(y, m, d).getTime();
    return startOfTarget < startOfToday;
  };

  const restoreDraftForLead = (leadId) => {
    try {
      if (!leadId) {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      }
      const raw = localStorage.getItem(`doxez_appointment_draft_${leadId}`) || sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const draft = JSON.parse(raw);
      if (draft.leadId && String(draft.leadId) !== String(leadId)) {
        return null;
      }
      return draft;
    } catch (e) {
      return null;
    }
  };

  const restoreDraft = () => {
    try {
      const activeLeadId = searchParams.get('leadId') || searchParams.get('fromLeadId') || formData.leadId;
      const draft = restoreDraftForLead(activeLeadId);
      if (!draft) return false;
      if (draft.formData) setFormData(draft.formData);
      if (draft.pricing) setPricing(draft.pricing);
      if (draft.doctorType) setDoctorType(draft.doctorType);
      if (draft.selectedCouponId) setSelectedCouponId(draft.selectedCouponId);
      if (draft.clinicalInfo) setClinicalInfo(draft.clinicalInfo);
      if (draft.communicationMode) setCommunicationMode(draft.communicationMode);
      if (draft.coverageMode) setCoverageMode(draft.coverageMode);
      if (draft.currentStep) {
        const idx = getStepIndex(draft.currentStep);
        if (idx >= 0 && idx < visibleSteps.length) {
          setCurrentStep(idx);
        }
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const urlStep = params.get(STEP_QUERY_KEY);
      if (urlStep) {
        const idx = getStepIndex(urlStep);
        if (idx >= 0) {
          setCurrentStep(idx);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!isInitializedRef.current) return;
    updateUrlStep(currentStep);
    persistDraft();
  }, [currentStep, formData, pricing, doctorType, selectedCouponId, clinicalInfo, communicationMode, coverageMode]);

  const handleMarkAsLost = async () => {
    if (!lostReason.trim()) {
      toast.error('Please select or specify a reason for marking as lost.');
      return;
    }
    setSubmittingLost(true);
    try {
      if (appointmentId) {
        await api.post(`/appointments/${appointmentId}/lost`, { failureReason: lostReason });
      }
      if (formData.leadId) {
        await api.patch(`/leads/${formData.leadId}`, {
          status: 'not_interested',
          closedReason: lostReason
        });
      }
      toast.success('Status updated: Lead & Appointment marked as Lost');
      setShowLostModal(false);
      navigate('/appointments');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSubmittingLost(false);
    }
  };

  const getDoctorDisplayName = (doc) => {
    if (!doc) return '';
    const firstName = doc.user?.firstName || doc.firstName || doc.name?.split(' ')[0] || '';
    const lastName = doc.user?.lastName || doc.lastName || doc.name?.split(' ').slice(1).join(' ') || '';
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();
    return doc.user?.name || doc.name || doc.doctorName || 'Doctor';
  };

  const packageDoctors = availabilityDate && availableDoctors.length > 0
    ? availableDoctors.filter(doc => doc.user?.doctorCategory !== 'inhouse')
    : doctors.filter(doc => doc.user?.doctorCategory !== 'inhouse');

  const inHouseDoctors = availabilityDate && availableDoctors.length > 0
    ? availableDoctors.filter(doc => doc.user?.doctorCategory === 'inhouse')
    : doctors.filter(doc => doc.user?.doctorCategory === 'inhouse');

  const visibleSteps = (doctorType === 'coupon' || pricing.finalPrice > 0)
    ? STEPS
    : STEPS.filter(s => s.key !== 'payment');

  const getStepIndex = (key) => visibleSteps.findIndex(s => s.key === key);

  useEffect(() => {
    const leadId = searchParams.get('leadId') || searchParams.get('fromLeadId');
    const editAppointmentId = searchParams.get('appointmentId');

    if (editAppointmentId) {
      setIsEditMode(true);
      setAppointmentId(editAppointmentId);
      setCurrentStep(0);
      api.get(`/appointments/${editAppointmentId}`).then(res => {
        const apt = res.data.data;
        setFormData({
          leadId: apt.leadId || '',
          patientName: apt.patientName || '',
          patientPhone: apt.patientPhone || '',
          patientEmail: apt.patientEmail || '',
          appointmentType: apt.appointmentType || 'consultation',
          communicationMode: apt.communicationMode || 'in_person',
          appointmentDate: apt.appointmentDate ? (typeof apt.appointmentDate === 'string' ? apt.appointmentDate.slice(0, 10) : new Date(apt.appointmentDate).toISOString().split('T')[0]) : '',
          appointmentTimeSlot: apt.appointmentTimeSlot || '',
          appointmentDoctorId: typeof apt.appointmentDoctorId === 'object' ? (apt.appointmentDoctorId?._id || '') : (apt.appointmentDoctorId || ''),
          notes: apt.notes || '',
          status: apt.status || 'draft'
        });
        setDoctorType(apt.couponId ? 'coupon' : 'in_house');
        setPricing({
          doctorFee: apt.originalPrice ?? 0,
          discountPercentage: apt.discountPercentage || 0,
          finalPrice: apt.finalPrice ?? 0,
          editing: false,
          platformFee: apt.platformFee || 0,
          otherCharges: apt.otherCharges || 0
        });
        if (apt.leadId) {
          api.get(`/leads/${apt.leadId}`).then(leadRes => {
            const lead = leadRes.data.data;
            setLeadSearch(`${lead.patientName} (${lead.bookingId || lead._id.slice(-8).toUpperCase()})`);
            setPreselectedLead(lead);
            setCoverageMode(getLeadCoverageMode(lead));
            const idProofDoc = (lead.documents || []).find(d => d.type === 'id_proof');
            setIdProof(idProofDoc ? { url: idProofDoc.url, name: idProofDoc.name } : null);
            const ayushmanDoc = (lead.documents || []).find(d => d.type === 'ayushman_card');
            setAyushmanCard(ayushmanDoc ? { url: ayushmanDoc.url, name: ayushmanDoc.name } : null);
            const medReports = (lead.documents || []).filter(d => d.type === 'medical_report' || !d.type);
            setMedicalReports(medReports.map(r => ({ url: r.url, name: r.name, type: r.type || 'medical_report' })));
          });
        }
        if (apt.couponId) {
          const coupon = typeof apt.couponId === 'object' ? apt.couponId : null;
          setSelectedCouponId(coupon ? coupon._id : apt.couponId);
          if (coupon) {
            setPricing({
              doctorFee: apt.originalPrice ?? doctorConsultationFee ?? 0,
              discountPercentage: apt.discountPercentage || 0,
              finalPrice: apt.finalPrice ?? 0,
              editing: true,
              taxType: apt.taxType || coupon.taxType || 'none',
              taxPercentage: apt.taxPercentage || coupon.taxPercentage || 0,
              taxName: apt.taxName || coupon.taxName || '',
              platformFee: apt.platformFee || 0,
              otherCharges: apt.otherCharges || 0
            });
            setCouponOriginal({
              consultationFee: apt.originalPrice ?? doctorConsultationFee ?? 0,
              discountPercentage: apt.discountPercentage || 0,
              taxType: apt.taxType || coupon.taxType || 'none',
              taxPercentage: apt.taxPercentage || coupon.taxPercentage || 0,
              taxName: apt.taxName || coupon.taxName || ''
            });
          }
        }
      }).catch(() => {
        toast.error('Failed to load appointment details');
      });
    } else if (leadId) {
      setFormData(prev => ({ ...prev, leadId }));

      const stepParam = searchParams.get('step');
      const savedStepKey = localStorage.getItem(`doxez_lead_last_step_${leadId}`);
      const savedDraft = restoreDraftForLead(leadId);

      let targetStepKey = stepParam || (savedDraft?.currentStep) || savedStepKey || 'clinical';
      let targetStepIdx = visibleSteps.findIndex(s => s.key === targetStepKey);
      if (targetStepIdx === -1) {
        targetStepIdx = 1;
      }
      setCurrentStep(targetStepIdx);

      if (savedDraft) {
        if (savedDraft.doctorType) setDoctorType(savedDraft.doctorType);
        if (savedDraft.selectedCouponId) setSelectedCouponId(savedDraft.selectedCouponId);
        if (savedDraft.pricing) setPricing(savedDraft.pricing);
        if (savedDraft.clinicalInfo) setClinicalInfo(savedDraft.clinicalInfo);
        if (savedDraft.coverageMode) setCoverageMode(savedDraft.coverageMode);
        if (savedDraft.communicationMode) setCommunicationMode(savedDraft.communicationMode);
        if (savedDraft.formData) {
          setFormData(prev => ({
            ...prev,
            ...savedDraft.formData,
            leadId
          }));
        }
      }

      api.get(`/leads/${leadId}`).then(res => {
        const lead = res.data.data;
        setFormData(prev => ({
          ...prev,
          leadId: lead._id,
          patientName: lead.patientName || prev.patientName || '',
          patientPhone: lead.patientPhone || prev.patientPhone || '',
          patientEmail: lead.patientEmail || prev.patientEmail || ''
        }));
        setLeadSearch(`${lead.patientName} (${lead.bookingId || lead._id.slice(-8).toUpperCase()})`);
        setPreselectedLead(lead);

        setClinicalInfo(prev => ({
          chiefComplaints: prev.chiefComplaints || lead.chiefComplaints || lead.disease || lead.treatmentRequired || '',
          pastMedicalHistory: prev.pastMedicalHistory || lead.pastMedicalHistory || '',
          comorbidities: prev.comorbidities || lead.comorbidities || ''
        }));

        setCoverageMode(prev => prev || getLeadCoverageMode(lead));

        const idProofDoc = (lead.documents || []).find(d => d.type === 'id_proof');
        setIdProof(idProofDoc ? { url: idProofDoc.url, name: idProofDoc.name } : null);
        const ayushmanDoc = (lead.documents || []).find(d => d.type === 'ayushman_card');
        setAyushmanCard(ayushmanDoc ? { url: ayushmanDoc.url, name: ayushmanDoc.name } : null);
        const medReports = (lead.documents || []).filter(d => d.type === 'medical_report' || !d.type);
        setMedicalReports(medReports.map(r => ({ url: r.url, name: r.name, type: r.type || 'medical_report' })));
      }).catch(() => {
        toast.error('Failed to load lead details');
      }).finally(() => {
        isInitializedRef.current = true;
      });
    } else {
      const restored = restoreDraft();
      const stepParam = searchParams.get('step');
      if (!restored && stepParam) {
        const idx = getStepIndex(stepParam);
        if (idx >= 0) setCurrentStep(idx);
      }
      isInitializedRef.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (!preselectedLead) return;
    const derived = getLeadCoverageMode(preselectedLead);
    if (derived && derived !== coverageMode) {
      setCoverageMode(derived);
    }
  }, [preselectedLead]);

  useEffect(() => {
    if (doctorType === 'in_house') {
      setPricing(prev => ({
        ...prev,
        doctorFee: 0,
        platformFee: 0,
        otherCharges: 0,
        discountPercentage: 0,
        finalPrice: 0,
        editing: false,
        taxType: 'none',
        taxPercentage: 0,
        taxName: '',
        netAmount: 0
      }));
      setSelectedCouponId('');
      setDoctorConsultationFee(0);
    } else if (doctorType === 'coupon' && !selectedCouponId) {
      setPricing(prev => {
        const pricingResult = calculateFromTotal(1000, prev.platformFee || 0, prev.otherCharges || 0, 0, 'cgst_sgst', 18);
        return {
          ...prev,
          ...pricingResult,
          editing: true,
          taxType: 'cgst_sgst',
          taxPercentage: 18,
          taxName: ''
        };
      });
    }
  }, [doctorType, selectedCouponId]);

  useEffect(() => {
    if (!appointmentId || !paymentLink) return;

    const MAX_RETRIES = 40;
    const RETRY_INTERVAL = 3000;
    let retryCount = 0;
    let stopped = false;

    const syncPayment = async () => {
      if (stopped) return;
      try {
        const res = await api.post(`/appointments/${appointmentId}/sync`);
        if (res.data?.success) {
          const status = res.data?.data?.appointment?.status;
          if (status === 'confirmed' || status === 'completed') {
            stopped = true;
            toast.success('Payment confirmed! Appointment confirmed.');
            navigate('/appointments');
            return;
          }
        }
        retryCount++;
        if (retryCount >= MAX_RETRIES) {
          stopped = true;
          toast.error('Payment verification timed out. Please contact support or try manual sync.');
          return;
        }
      } catch (error) {
        retryCount++;
        if (retryCount >= MAX_RETRIES) {
          stopped = true;
          toast.error('Payment verification timed out. Please contact support or try manual sync.');
          return;
        }
      }
    };

    const timer = setInterval(syncPayment, RETRY_INTERVAL);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [appointmentId, paymentLink, navigate]);

  useEffect(() => {
    const fetchDoctorFee = async () => {
      if (!formData.appointmentDoctorId) return;
      try {
        const res = await api.get(`/doctors/${formData.appointmentDoctorId}`);
        const doctorData = res.data.data;
        const fee = Number(doctorData?.financials?.consultationFee || 0);
        setDoctorConsultationFee(fee);
        if (doctorType !== 'coupon' || !selectedCouponId) {
          setPricing(prev => ({
            ...prev,
            doctorFee: fee
          }));
        }
      } catch {
        // keep existing pricing if fetch fails
      }
    };
    fetchDoctorFee();
  }, [formData.appointmentDoctorId]);

  useEffect(() => {
    if (!pricing.editing) return;
    const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage);
    setPricing(prev => ({ ...prev, ...pricingResult }));
  }, [pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage, pricing.editing]);

  const fetchOptions = async () => {
    try {
      const docRes = await api.get('/doctors?limit=1000');
      const rawDoctors = docRes.data?.data?.doctors || docRes.data?.doctors || docRes.data?.data || docRes.data || [];
      setDoctors(Array.isArray(rawDoctors) ? rawDoctors : []);
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
    }

    try {
      const pkgRes = await getCoupons({ isActive: true });
      const rawCoupons = pkgRes?.data?.coupons || pkgRes?.data || [];
      setCoupons(Array.isArray(rawCoupons) ? rawCoupons : []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setFetchingOptions(false);
    }
  };

  const fetchAvailabilityForecast = async () => {
    if (!availabilityDate) {
      setAvailableDoctors([]);
      setForecast([]);
      return;
    }
    setLoadingForecast(true);
    try {
      const response = await getDoctorAvailabilityForecast({
        date: availabilityDate,
        doctorType: availabilityDoctorType || undefined
      });
      const forecastData = response.data || [];
      setForecast(forecastData);
      const available = forecastData.filter(item => item.isAvailableOnTargetDate && !item.needsRosterUpdate);
      setAvailableDoctors(available);
    } catch (error) {
      console.error('Failed to load availability forecast:', error);
      setForecast([]);
      setAvailableDoctors([]);
    } finally {
      setLoadingForecast(false);
    }
  };

  const searchLeads = async () => {
    if (!leadSearch.trim()) {
      setLeads([]);
      return;
    }
    setSearchingLeads(true);
    try {
      const res = await api.get(`/leads?search=${encodeURIComponent(leadSearch)}&limit=20`);
      setLeads(res.data.data.leads || []);
    } catch (error) {
      console.error('Failed to search leads:', error);
    } finally {
      setSearchingLeads(false);
    }
  };

  const handleClinicalFileUpload = async (files, type) => {
    if (!files || files.length === 0) return;
    const MAX_SIZE = 50 * 1024 * 1024;
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`File "${file.name}" exceeds 50MB limit.`);
        return;
      }
    }
    if (type === 'medical' && (medicalReports.length + files.length > 15)) {
      toast.error('Limit: 15 reports');
      return;
    }
    const fileFormData = new FormData();
    files.forEach(f => fileFormData.append('documents', f));
    try {
      setUploading(true);
      const response = await api.post('/upload', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedFiles = response.data?.data?.files || response.data?.files || [];
      if (!uploadedFiles || uploadedFiles.length === 0) {
        throw new Error('No files returned from server');
      }
      if (type === 'id_proof') {
        setIdProof({ url: uploadedFiles[0].url, name: uploadedFiles[0].filename });
        toast.success('Identity Proof Attached');
      } else if (type === 'ayushman') {
        setAyushmanCard({ url: uploadedFiles[0].url, name: uploadedFiles[0].filename });
        toast.success('Ayushman Card Attached');
      } else if (type === 'medical') {
        const newReports = uploadedFiles.map(f => ({ url: f.url, name: f.filename, type: 'medical_report' }));
        setMedicalReports(prev => [...prev, ...newReports]);
        toast.success(`${uploadedFiles.length} Report(s) Added`);
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFilePreview = (file) => {
    if (!file || !file.url) return;
    const isPDF = file.url.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
    setPreviewFile({
      url: file.url,
      name: file.name || 'Document',
      isPDF
    });
  };

  const handleInsertClinicalTag = (field, tagText) => {
    setClinicalInfo(prev => {
      const currentVal = (prev[field] || '').trim();
      if (!currentVal) {
        return { ...prev, [field]: tagText };
      }
      if (currentVal.toLowerCase().includes(tagText.toLowerCase())) {
        return prev;
      }
      return { ...prev, [field]: `${currentVal}, ${tagText}` };
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (leadSearch.trim()) searchLeads();
    }, 400);
    return () => clearTimeout(timer);
  }, [leadSearch]);

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchAvailabilityForecast();
  }, [availabilityDate, availabilityDoctorType]);

  useEffect(() => {
    const maxIndex = visibleSteps.length - 1;
    if (currentStep > maxIndex) {
      setCurrentStep(maxIndex);
    }
  }, [doctorType, currentStep, visibleSteps.length, pricing.finalPrice]);

  const canProceedFromStep = (step) => {
    if (appointmentExistsError) return false;
    switch (step) {
      case 'lead':
        return !!formData.leadId;
      case 'clinical':
        return true;
      case 'schedule':
        return !!formData.appointmentDate && !!formData.appointmentTimeSlot;
      case 'doctor_type':
        if (doctorType === 'coupon') {
          return !!formData.appointmentDoctorId;
        }
        return !!formData.appointmentDoctorId;
      case 'payment':
        if (pricing.finalPrice === 0) return true;
        return !!paymentLink;
      case 'confirm':
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    const key = visibleSteps[currentStep]?.key;
    if (!key) return;

    if (key === 'payment' && !paymentLink && pricing.finalPrice > 0) {
      toast.error('Please click "Generate Payment Link" to generate and send the payment link to the patient.');
      return;
    }

    if (key === 'clinical') {
      await syncClinicalInfoToLead();
    }

    const nextIdx = currentStep + 1;
    const activeLeadId = formData.leadId || preselectedLead?._id || searchParams.get('leadId') || searchParams.get('fromLeadId');

    if (isEditMode && appointmentId) {
      if (key === 'doctor_type' && doctorType === 'coupon') {
        await updateAppointmentDoctor();
      }
      await createAppointmentDraft();
      if (nextIdx < visibleSteps.length) {
        if (activeLeadId && visibleSteps[nextIdx]?.key) {
          localStorage.setItem(`doxez_lead_last_step_${activeLeadId}`, visibleSteps[nextIdx].key);
        }
        setCurrentStep(nextIdx);
        updateUrlStep(nextIdx);
      }
      return;
    }

    if (key === 'schedule' && canProceedFromStep('schedule')) {
      const newAppointmentId = await createAppointmentDraft();
      if (newAppointmentId && nextIdx < visibleSteps.length) {
        if (activeLeadId && visibleSteps[nextIdx]?.key) {
          localStorage.setItem(`doxez_lead_last_step_${activeLeadId}`, visibleSteps[nextIdx].key);
        }
        setCurrentStep(nextIdx);
        updateUrlStep(nextIdx);
      }
      return;
    }

    if (nextIdx < visibleSteps.length) {
      if (activeLeadId && visibleSteps[nextIdx]?.key) {
        localStorage.setItem(`doxez_lead_last_step_${activeLeadId}`, visibleSteps[nextIdx].key);
      }
      setCurrentStep(nextIdx);
      updateUrlStep(nextIdx);
    }
  };

  const syncClinicalInfoToLead = async () => {
    const leadTargetId = preselectedLead?._id || formData.leadId;
    if (!leadTargetId) return;
    const hasClinicalData = clinicalInfo.chiefComplaints || clinicalInfo.pastMedicalHistory || clinicalInfo.comorbidities;
    const hasDocuments = idProof || ayushmanCard || medicalReports.length > 0;
    const hasCoverage = !!coverageMode;
    if (!hasClinicalData && !hasDocuments && !hasCoverage) return;
    try {
      const payload = {
        chiefComplaints: clinicalInfo.chiefComplaints,
        pastMedicalHistory: clinicalInfo.pastMedicalHistory,
        comorbidities: clinicalInfo.comorbidities,
        coverageMode
      };
      const docs = [];
      if (ayushmanCard) {
        docs.push({ ...ayushmanCard, type: 'ayushman_card', source: 'CC' });
      }
      if (idProof) {
        docs.push({ ...idProof, type: 'id_proof', source: 'CC' });
      }
      if (medicalReports.length > 0) {
        docs.push(...medicalReports.map(r => ({ ...r, source: 'CC' })));
      }
      if (docs.length > 0) {
        payload.documents = docs;
      }
      await api.patch(`/leads/${leadTargetId}`, payload);
      toast.success('Clinical information saved to lead');
    } catch (error) {
      console.error('Failed to sync clinical info:', error);
    }
  };

  const generatePaymentLink = async () => {
    let currentAptId = appointmentId;
    if (!currentAptId) {
      currentAptId = await createAppointmentDraft();
    }
    if (!currentAptId) {
      toast.error('Please complete schedule and doctor details first');
      return;
    }
    await syncClinicalInfoToLead();
    setGeneratingPayment(true);
    try {
      const res = await api.post(`/appointments/${currentAptId}/payment-link`, {
        patientEmail: formData.patientEmail || undefined,
        forceRegenerate: true
      });
      if (res.data?.success) {
        const data = res.data.data || {};
        setPaymentLink(data.razorpayDetails?.paymentLinkUrl || '');
        if (data.doctorFee !== undefined || data.finalPrice !== undefined) {
          setPricing(prev => ({
            ...prev,
            doctorFee: data.doctorFee ?? prev.doctorFee,
            discountPercentage: data.discountPercentage ?? prev.discountPercentage,
            finalPrice: data.finalPrice ?? prev.finalPrice,
            taxType: data.taxType ?? prev.taxType,
            taxPercentage: data.taxPercentage ?? prev.taxPercentage,
            taxName: data.taxName ?? prev.taxName,
            platformFee: data.platformFee ?? prev.platformFee,
            otherCharges: data.otherCharges ?? prev.otherCharges
          }));
        }
        toast.success('Payment link generated & sent to patient');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate payment link');
    } finally {
      setGeneratingPayment(false);
    }
  };

  const updateAppointmentDoctor = async () => {
    if (!appointmentId || !formData.appointmentDoctorId) return;
    setLoading(true);
    try {
      const res = await api.patch(`/appointments/${appointmentId}`, {
        appointmentDoctorId: formData.appointmentDoctorId
      });
      if (res.data?.success) {
        toast.success('Doctor assigned successfully');
        setCurrentStep(prev => prev + 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign doctor');
    } finally {
      setLoading(false);
    }
  };

  const saveCouponEditsIfAny = async () => {
    if (!selectedCouponId || !couponOriginal || !user?.role) return;
    if (!['admin', 'customer_care', 'cc_head'].includes(user.role)) return;

    const hasDiscountPercentageChanged = pricing.discountPercentage !== couponOriginal.discountPercentage;
    const hasTaxTypeChanged = pricing.taxType !== couponOriginal.taxType;
    const hasTaxPercentageChanged = pricing.taxPercentage !== couponOriginal.taxPercentage;
    const hasTaxNameChanged = pricing.taxName !== couponOriginal.taxName;

    if (!hasDiscountPercentageChanged && !hasTaxTypeChanged && !hasTaxPercentageChanged && !hasTaxNameChanged) {
      return;
    }

    const payload = {
      discountPercentage: pricing.discountPercentage || undefined,
      taxType: pricing.taxType || 'none',
      taxPercentage: pricing.taxPercentage || undefined,
      taxName: pricing.taxName || undefined
    };

    await updateCoupon(selectedCouponId, payload);
    setCoupons(prev => prev.map(c => c._id === selectedCouponId ? { ...c, ...payload } : c));
    setCouponOriginal({
      discountPercentage: pricing.discountPercentage,
      taxType: pricing.taxType,
      taxPercentage: pricing.taxPercentage,
      taxName: pricing.taxName
    });
  };

  const calculateFromTotal = (totalInvoice, platformCharges, otherCharges, discountPercentage, taxType, taxPercentage) => {
    const total = Number(totalInvoice || 0);
    const platform = Number(platformCharges || 0);
    const other = Number(otherCharges || 0);
    const discount = Number(discountPercentage || 0);
    const taxPct = taxType !== 'none' ? Number(taxPercentage || 0) : 0;

    const discountAmount = platform > 0 && discount > 0 ? Math.round((platform * discount) / 100) : 0;
    const discountedPlatform = Math.max(0, platform - discountAmount);

    const taxAmount = Math.round((total - (total / (1 + taxPct / 100))) * 100) / 100;
    const baseAmount = Math.round((total - taxAmount) * 100) / 100;

    const consultationFee = Math.max(0, Math.round((baseAmount - discountedPlatform - other) * 100) / 100);

    return {
      doctorFee: consultationFee,
      platformFee: discountedPlatform,
      otherCharges: other,
      finalPrice: total,
      taxAmount,
      subtotal: baseAmount,
      netAmount: baseAmount
    };
  };

  const createAppointmentDraft = async () => {
    setLoading(true);
    try {
      const date = formData.appointmentDate;
      let yyyy, mm, dd;
      if (date) {
        if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
          [yyyy, mm, dd] = date.split('T')[0].split('-');
        } else if (/^\d{2}-\d{2}-\d{4}/.test(date)) {
          [dd, mm, yyyy] = date.split('-');
        }
      }
      const isoDate = (yyyy && mm && dd) ? `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:00:00.000Z` : (date || '');

      const selectedCoupon = coupons.find(p => p._id === selectedCouponId);

      if (selectedCouponId && couponOriginal) {
        await saveCouponEditsIfAny();
      }

      if (isEditMode && appointmentId) {
        const payload = {
          ...(formData.leadId ? { leadId: formData.leadId } : {}),
          appointmentDate: isoDate || undefined,
          appointmentTimeSlot: formData.appointmentTimeSlot || undefined,
          appointmentType: formData.appointmentType,
          communicationMode: formData.communicationMode,
          ...(formData.appointmentDoctorId ? { appointmentDoctorId: formData.appointmentDoctorId } : {}),
          notes: formData.notes || undefined,
          ...(selectedCoupon ? { couponId: selectedCoupon._id } : {}),
          doctorFee: pricing.doctorFee ?? 0,
          platformFee: pricing.platformFee ?? 0,
          otherCharges: pricing.otherCharges ?? 0,
          taxPercentage: pricing.taxPercentage || undefined,
          finalPrice: pricing.finalPrice || undefined
        };
        const res = await api.patch(`/appointments/${appointmentId}`, payload);
        if (res.data?.success) {
          toast.success('Appointment updated successfully');
        }
        return appointmentId;
      } else {
        const payload = {
          leadId: formData.leadId,
          patientName: formData.patientName,
          patientPhone: formData.patientPhone,
          patientEmail: formData.patientEmail || undefined,
          appointmentType: formData.appointmentType,
          communicationMode: formData.communicationMode,
          appointmentDate: isoDate,
          appointmentTimeSlot: formData.appointmentTimeSlot,
          notes: formData.notes || undefined,
          ...(formData.appointmentDoctorId ? { appointmentDoctorId: formData.appointmentDoctorId } : {}),
          ...(selectedCoupon ? { couponId: selectedCoupon._id } : {}),
          doctorFee: pricing.doctorFee ?? 0,
          platformFee: pricing.platformFee ?? 0,
          otherCharges: pricing.otherCharges ?? 0,
          taxPercentage: pricing.taxPercentage || undefined,
          finalPrice: pricing.finalPrice || undefined
        };

        const res = await api.post('/appointments', payload);
        if (res.data?.success) {
          const newId = res.data.data._id;
          setAppointmentId(newId);
          toast.success(isEditMode ? 'Appointment updated' : 'Appointment draft created');
          return newId;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save appointment');
    } finally {
      setLoading(false);
    }
    return null;
  };

  const handleConfirmAppointment = async () => {
    if (!appointmentId && !isEditMode) return;
    setLoading(true);
    try {
      if (isEditMode && appointmentId) {
        await createAppointmentDraft();
        toast.success('Appointment changes saved');
      } else {
        await api.post(`/appointments/${appointmentId}/confirm`);
        toast.success('Appointment confirmed successfully');
      }
      clearDraft();
      navigate('/appointments');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process appointment');
    } finally {
      setLoading(false);
    }
  };

  const isStepComplete = (stepKey) => {
    const idx = visibleSteps.findIndex(s => s.key === stepKey);
    return idx < currentStep;
  };

  const isStepActive = (stepKey) => {
    return visibleSteps[currentStep]?.key === stepKey;
  };

  const renderPremiumStepper = () => (
    <div className="flex items-center w-full">
      {visibleSteps.map((step, index) => {
        const isComplete = isStepComplete(step.key);
        const isActive = isStepActive(step.key);
        const isLast = index === visibleSteps.length - 1;
        const isAccessible = isComplete || index <= currentStep || (formData.leadId && index <= 3);

        return (
          <div key={step.key} className="flex items-center flex-1">
            <div
              onClick={() => {
                if (isAccessible) {
                  setCurrentStep(index);
                  updateUrlStep(index);
                }
              }}
              className={`flex flex-col items-center relative ${
                isAccessible ? 'cursor-pointer group' : 'cursor-not-allowed opacity-80'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300 ${isComplete
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200 group-hover:scale-105'
                  : isActive
                    ? 'bg-[#143E78] text-white shadow-lg shadow-[#143E78]/30 scale-110'
                    : isAccessible
                      ? 'bg-slate-100 text-slate-600 border border-slate-300 group-hover:border-[#143E78] group-hover:text-[#143E78]'
                      : 'bg-slate-100 text-slate-400 border border-slate-200'
                  }`}
              >
                {isComplete ? <CheckCircle size={20} /> : (
                  <span className="text-lg">{step.icon}</span>
                )}
              </div>
              <span
                className={`text-[10px] mt-2 font-black uppercase tracking-widest whitespace-nowrap transition-colors ${isComplete
                  ? 'text-emerald-600'
                  : isActive
                    ? 'text-[#143E78]'
                    : isAccessible
                      ? 'text-slate-600 group-hover:text-[#143E78]'
                      : 'text-slate-400'
                  }`}
              >
                {step.label}
              </span>
            </div>
            {!isLast && (
              <div className="flex-1 mx-2 mb-6">
                <div className="h-1 rounded-full bg-slate-100 relative overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-400 w-full' : 'bg-slate-200 w-full'
                      }`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderPremiumInput = (label, type = 'text', value, onChange, placeholder, required, icon, error, options) => {
    const inputId = label.toLowerCase().replace(/\s+/g, '-');
    const hasError = Boolean(error);

    if (type === 'select') {
      return (
        <div className="space-y-2">
          <label htmlFor={inputId} className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
            {icon && <span className="text-[#143E78]">{icon}</span>}
            {label}
            {required && <span className="text-rose-500">*</span>}
          </label>
          <div className="relative">
            <select
              id={inputId}
              value={value}
              onChange={onChange}
              className={`w-full px-4 py-3.5 bg-white border-2 rounded-2xl font-bold text-sm outline-none transition-all appearance-none ${hasError
                ? 'border-rose-300 focus:border-rose-500'
                : 'border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10'
                }`}
            >
              {options}
            </select>
            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          {hasError && <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mt-1">{error}</p>}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <label htmlFor={inputId} className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
          {icon && <span className="text-[#143E78]">{icon}</span>}
          {label}
          {required && <span className="text-rose-500">*</span>}
        </label>
        <input
          id={inputId}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full px-4 py-3.5 bg-white border-2 rounded-2xl font-bold text-sm outline-none transition-all ${hasError
            ? 'border-rose-300 focus:border-rose-500'
            : 'border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10'
            }`}
        />
        {hasError && <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mt-1">{error}</p>}
      </div>
    );
  };

  const renderPremiumCardHeader = (title, subtitle, icon, accentColor = '#143E78') => (
    <div className="flex items-start gap-4 mb-6">
      <div className="p-3 rounded-2xl bg-[#143E78]/10 border border-[#143E78]/20 shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-xl font-black text-slate-900 tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );

  const renderLeadCard = (lead) => {
    const isSelected = formData.leadId === lead._id;
    const checkExistingAppointment = async () => {
      try {
        const res = await api.get(`/appointments?leadId=${lead._id}&limit=1`);
        const appointments = res.data?.data?.appointments || [];
        if (appointments.length > 0) {
          const existing = appointments[0];
          if (!['draft', 'pending_payment'].includes(existing.status)) {
            setAppointmentExistsError(`Appointment already exists for this lead (Status: ${existing.status}). Please complete/cancel it before creating a new one.`);
            return;
          }
          setAppointmentExistsError('');
          return;
        }
        setAppointmentExistsError('');
      } catch (error) {
        console.error('Failed to check existing appointment:', error);
      }
    };

    const selectLead = async () => {
      const leadId = lead._id;
      setFormData(prev => ({
        ...prev,
        leadId: lead._id,
        patientName: lead.patientName || '',
        patientPhone: lead.patientPhone || '',
        patientEmail: lead.patientEmail || ''
      }));
      setLeadSearch(`${lead.patientName} (${lead.bookingId || lead._id.slice(-8).toUpperCase()})`);
      setLeads([]);
      checkExistingAppointment();
      setPreselectedLead(lead);

      const savedDraft = restoreDraftForLead(leadId);
      const savedStepKey = localStorage.getItem(`doxez_lead_last_step_${leadId}`);
      let targetStepKey = (savedDraft?.currentStep) || savedStepKey || 'clinical';
      let targetStepIdx = visibleSteps.findIndex(s => s.key === targetStepKey);
      if (targetStepIdx <= 0) targetStepIdx = 1;

      if (savedDraft) {
        if (savedDraft.doctorType) setDoctorType(savedDraft.doctorType);
        if (savedDraft.selectedCouponId) setSelectedCouponId(savedDraft.selectedCouponId);
        if (savedDraft.pricing) setPricing(savedDraft.pricing);
        if (savedDraft.clinicalInfo) setClinicalInfo(savedDraft.clinicalInfo);
        if (savedDraft.coverageMode) setCoverageMode(savedDraft.coverageMode);
        if (savedDraft.communicationMode) setCommunicationMode(savedDraft.communicationMode);
        if (savedDraft.formData) {
          setFormData(prev => ({
            ...prev,
            ...savedDraft.formData,
            leadId
          }));
        }
      } else {
        setClinicalInfo({
          chiefComplaints: lead.chiefComplaints || lead.disease || lead.treatmentRequired || '',
          pastMedicalHistory: lead.pastMedicalHistory || '',
          comorbidities: lead.comorbidities || ''
        });
        setCoverageMode(getLeadCoverageMode(lead));
      }

      const idProofDoc = (lead.documents || []).find(d => d.type === 'id_proof');
      setIdProof(idProofDoc ? { url: idProofDoc.url, name: idProofDoc.name } : null);
      const ayushmanDoc = (lead.documents || []).find(d => d.type === 'ayushman_card');
      setAyushmanCard(ayushmanDoc ? { url: ayushmanDoc.url, name: ayushmanDoc.name } : null);
      const medReports = (lead.documents || []).filter(d => d.type === 'medical_report' || !d.type);
      setMedicalReports(medReports.map(r => ({ url: r.url, name: r.name, type: r.type || 'medical_report' })));

      setCurrentStep(targetStepIdx);
      updateUrlStep(targetStepIdx);
    };

    return (
      <div
        onClick={selectLead}
        className={`relative p-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${isSelected
          ? 'bg-[#143E78]/5 border-[#143E78] shadow-lg shadow-[#143E78]/10'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
          }`}
      >
        {isSelected && (
          <div className="absolute top-3 right-3">
            <BadgeCheck className="w-5 h-5 text-[#143E78]" />
          </div>
        )}
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${isSelected ? 'bg-[#143E78] text-white' : 'bg-slate-100 text-slate-500'
            }`}>
            <User size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-slate-900 truncate">{lead.patientName}</p>
            <p className="text-xs font-bold text-slate-500 mt-0.5 flex items-center gap-1">
              <Phone size={12} /> {lead.patientPhone}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-lg text-[9px] font-black uppercase tracking-wider">
                {lead.bookingId || `#${lead._id.slice(-8).toUpperCase()}`}
              </span>
              <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${lead.status === 'new' ? 'bg-blue-50 text-blue-600' :
                lead.status === 'screening' ? 'bg-amber-50 text-amber-600' :
                  lead.status === 'patient_ready' ? 'bg-emerald-50 text-emerald-600' :
                    'bg-slate-100 text-slate-600'
                }`}>
                {lead.status?.replace(/_/g, ' ') || 'new'}
              </span>
            </div>
          </div>
        </div>
        {(lead.treatmentRequired || lead.disease) && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-slate-500">{lead.treatmentRequired || lead.disease}</p>
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    const stepKey = visibleSteps[currentStep]?.key;
    if (!stepKey) {
      const fallbackKey = visibleSteps[0]?.key;
      if (fallbackKey) {
        return renderStepContentFor(fallbackKey);
      }
      return null;
    }
    return renderStepContentFor(stepKey);
  };

  const renderStepContentFor = (stepKey) => {
    switch (stepKey) {
      case 'lead':
        return (
          <div className="space-y-6">
            {renderPremiumCardHeader(isEditMode ? 'Linked Patient Lead' : 'Select Patient Lead', isEditMode ? (['admin', 'customer_care', 'cc_head'].includes(user?.role) ? 'Update linked lead if needed' : 'Existing lead linked to this appointment') : 'Choose existing lead for appointment', <User size={24} />)}
            {appointmentExistsError && (
              <div className="p-4 bg-rose-50 border-2 border-rose-200 rounded-2xl flex items-start gap-3">
                <div className="p-2 bg-rose-100 rounded-xl shrink-0">
                  <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <div>
                  <p className="text-sm font-black text-rose-800">Appointment Already Exists</p>
                  <p className="text-xs font-bold text-rose-600 mt-0.5">{appointmentExistsError}</p>
                </div>
              </div>
            )}
            {isEditMode && !['admin', 'customer_care', 'cc_head'].includes(user?.role) && preselectedLead ? (
              <div className="p-5 bg-[#143E78]/5 rounded-2xl border-2 border-[#143E78]/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#143E78] text-white flex items-center justify-center shrink-0">
                    <User size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-slate-900 truncate">{preselectedLead.patientName}</p>
                    <p className="text-xs font-bold text-slate-500 mt-0.5 flex items-center gap-1">
                      <Phone size={12} /> {preselectedLead.patientPhone}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-lg text-[9px] font-black uppercase tracking-wider">
                        {preselectedLead.bookingId || `#${preselectedLead._id.slice(-8).toUpperCase()}`}
                      </span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-lg text-[9px] font-black uppercase tracking-wider">
                        {preselectedLead.status?.replace(/_/g, ' ') || 'new'}
                      </span>
                    </div>
                  </div>
                </div>
                {(preselectedLead.treatmentRequired || preselectedLead.disease) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-bold text-slate-500">{preselectedLead.treatmentRequired || preselectedLead.disease}</p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search by name, phone, or booking ID..."
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    className="w-full pl-11 pr-4 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                  />
                </div>
                {searchingLeads && (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 size={28} className="animate-spin text-[#143E78]" />
                  </div>
                )}
                {!searchingLeads && leads.length > 0 && (
                  <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-1">
                    {leads.map(lead => renderLeadCard(lead))}
                  </div>
                )}
                {!searchingLeads && leadSearch && leads.length === 0 && (
                  <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                    <Search size={36} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm font-black text-slate-500 uppercase tracking-wider">No leads found matching "{leadSearch}"</p>
                    <p className="text-xs font-bold text-slate-400 mt-1">Try searching by patient name or phone number</p>
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 'clinical':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* 2-Column Responsive Workspace */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

              {/* LEFT COLUMN: Clinical Notes & History (7 Cols) */}
              <div className="lg:col-span-7 space-y-5">

                {/* Chief Complaints Card */}
                <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600 shrink-0">
                        <Heart size={16} />
                      </div>
                      <div>
                        <label className="text-sm font-black text-slate-900 tracking-tight flex items-center gap-1.5">
                          Chief Complaints & Symptoms
                        </label>
                      </div>
                    </div>
                    {clinicalInfo.chiefComplaints && (
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                        {clinicalInfo.chiefComplaints.length} chars
                      </span>
                    )}
                  </div>

                  <textarea
                    value={clinicalInfo.chiefComplaints}
                    onChange={(e) => setClinicalInfo(prev => ({ ...prev, chiefComplaints: e.target.value }))}
                    placeholder="Describe patient's symptoms, duration, pain scale, or reasons for surgical consultation..."
                    rows={4}
                    className="w-full px-4 py-3.5 bg-slate-50/50 hover:bg-white focus:bg-white border-2 border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 rounded-2xl font-bold text-sm text-slate-800 outline-none transition-all resize-none shadow-inner"
                  />
                </div>

                {/* Past Medical History Card */}
                <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                        <ClipboardCheck size={16} />
                      </div>
                      <div>
                        <label className="text-sm font-black text-slate-900 tracking-tight">
                          Past Medical & Surgical History
                        </label>
                      </div>
                    </div>
                    {clinicalInfo.pastMedicalHistory && (
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                        {clinicalInfo.pastMedicalHistory.length} chars
                      </span>
                    )}
                  </div>

                  <textarea
                    value={clinicalInfo.pastMedicalHistory}
                    onChange={(e) => setClinicalInfo(prev => ({ ...prev, pastMedicalHistory: e.target.value }))}
                    placeholder="Document prior surgeries, major illnesses, drug allergies (e.g., Penicillin), past hospital admissions..."
                    rows={3}
                    className="w-full px-4 py-3.5 bg-slate-50/50 hover:bg-white focus:bg-white border-2 border-slate-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/10 rounded-2xl font-bold text-sm text-slate-800 outline-none transition-all resize-none shadow-inner"
                  />
                </div>

                {/* Comorbidities Card */}
                <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                        <AlertCircle size={16} />
                      </div>
                      <div>
                        <label className="text-sm font-black text-slate-900 tracking-tight">
                          Comorbidities & Chronic Risk Factors
                        </label>
                      </div>
                    </div>
                    {clinicalInfo.comorbidities && (
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                        {clinicalInfo.comorbidities.length} chars
                      </span>
                    )}
                  </div>

                  <textarea
                    value={clinicalInfo.comorbidities}
                    onChange={(e) => setClinicalInfo(prev => ({ ...prev, comorbidities: e.target.value }))}
                    placeholder="Document ongoing chronic conditions, blood pressure stability, regular insulin/medications..."
                    rows={3}
                    className="w-full px-4 py-3.5 bg-slate-50/50 hover:bg-white focus:bg-white border-2 border-slate-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 rounded-2xl font-bold text-sm text-slate-800 outline-none transition-all resize-none shadow-inner"
                  />
                </div>
              </div>

              {/* RIGHT COLUMN: Coverage & Documents Vault (5 Cols) */}
              <div className="lg:col-span-5 space-y-5">

                {/* Identity & Eligibility Verification Card */}
                <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                      <BadgeCheck size={16} />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-slate-900 tracking-tight">Identity & Verification</h4>
                      <p className="text-[11px] font-bold text-slate-400">Govt ID & eligibility cards</p>
                    </div>
                  </div>

                  {/* Patient ID Proof */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs font-black text-slate-600 uppercase tracking-wider">
                      <span className="flex items-center gap-1.5">
                        <FileCheck size={14} className="text-[#143E78]" />
                        Patient ID Proof (Aadhaar / Voter)
                      </span>
                      {idProof && (
                        <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                          Attached
                        </span>
                      )}
                    </div>
                    <input
                      type="file"
                      id="id-proof-upload"
                      className="hidden"
                      accept=".pdf,image/*"
                      onChange={(e) => handleClinicalFileUpload(Array.from(e.target.files), 'id_proof')}
                    />
                    {idProof ? (
                      <div className="flex items-center justify-between p-3 bg-emerald-50/70 rounded-2xl border-2 border-emerald-200 shadow-sm group">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                            <CheckCircle2 size={16} />
                          </div>
                          <span className="text-xs font-bold text-slate-800 truncate">{idProof.name || 'ID Proof Attached'}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleFilePreview(idProof)}
                            className="p-1.5 text-emerald-700 hover:text-emerald-900 bg-white hover:bg-emerald-100 rounded-xl border border-emerald-200 transition-all shadow-sm"
                            title="Preview Document"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setIdProof(null)}
                            className="p-1.5 text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 rounded-xl border border-rose-200 transition-all shadow-sm"
                            title="Remove Document"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => document.getElementById('id-proof-upload')?.click()}
                        disabled={uploading}
                        className="w-full px-4 py-3 border-2 border-dashed border-slate-200 hover:border-[#143E78] bg-slate-50/50 hover:bg-[#143E78]/5 rounded-2xl text-xs font-black text-slate-600 hover:text-[#143E78] transition-all flex items-center justify-center gap-2 active:scale-98"
                      >
                        <Upload size={15} /> Upload Patient ID Proof
                      </button>
                    )}
                  </div>

                  {/* Ayushman Card (Shown when coverage is Ayushman or if card exists) */}
                  {(coverageMode === 'ayushman' || ayushmanCard) && (
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs font-black text-slate-600 uppercase tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <Shield size={14} className="text-amber-500" />
                          Ayushman Card (PM-JAY)
                        </span>
                        {ayushmanCard && (
                          <span className="text-[10px] font-black text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                            Attached
                          </span>
                        )}
                      </div>
                      <input
                        type="file"
                        id="ayushman-card-upload"
                        className="hidden"
                        accept=".pdf,image/*"
                        onChange={(e) => handleClinicalFileUpload(Array.from(e.target.files), 'ayushman')}
                      />
                      {ayushmanCard ? (
                        <div className="flex items-center justify-between p-3 bg-amber-50/80 rounded-2xl border-2 border-amber-200 shadow-sm group">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
                              <CheckCircle2 size={16} />
                            </div>
                            <span className="text-xs font-bold text-slate-800 truncate">{ayushmanCard.name || 'Ayushman Card Attached'}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleFilePreview(ayushmanCard)}
                              className="p-1.5 text-amber-700 hover:text-amber-900 bg-white hover:bg-amber-100 rounded-xl border border-amber-200 transition-all shadow-sm"
                              title="Preview Document"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setAyushmanCard(null)}
                              className="p-1.5 text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 rounded-xl border border-rose-200 transition-all shadow-sm"
                              title="Remove Document"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => document.getElementById('ayushman-card-upload')?.click()}
                          disabled={uploading}
                          className="w-full px-4 py-3 border-2 border-dashed border-amber-200 hover:border-amber-500 bg-amber-50/40 hover:bg-amber-50 rounded-2xl text-xs font-black text-amber-800 transition-all flex items-center justify-center gap-2 active:scale-98"
                        >
                          <Upload size={15} /> Upload Ayushman Card
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Diagnostic Reports Vault Card */}
                <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 shrink-0">
                        <FileText size={16} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-900 tracking-tight">Diagnostic Reports Vault</h4>
                        <p className="text-[11px] font-bold text-slate-400">Scans, ultrasound, blood tests, prescriptions</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-wider">
                      {medicalReports.length} / 15
                    </span>
                  </div>

                  <input
                    type="file"
                    id="medical-reports-upload"
                    className="hidden"
                    accept=".pdf,image/*"
                    multiple
                    onChange={(e) => handleClinicalFileUpload(Array.from(e.target.files), 'medical')}
                  />

                  {/* Drag & Drop Upload Zone */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDraggingReports(true); }}
                    onDragLeave={() => setIsDraggingReports(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDraggingReports(false);
                      if (e.dataTransfer.files?.length) {
                        handleClinicalFileUpload(Array.from(e.dataTransfer.files), 'medical');
                      }
                    }}
                    onClick={() => document.getElementById('medical-reports-upload')?.click()}
                    className={`p-5 rounded-2xl border-2 border-dashed text-center cursor-pointer transition-all ${isDraggingReports
                      ? 'border-[#143E78] bg-[#143E78]/10 scale-[1.01]'
                      : 'border-slate-200 hover:border-[#143E78] bg-slate-50/50 hover:bg-[#143E78]/5'
                      }`}
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center justify-center py-2 gap-2">
                        <Loader2 size={24} className="animate-spin text-[#143E78]" />
                        <p className="text-xs font-black text-[#143E78] uppercase tracking-wider">Uploading documents...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-1.5">
                        <div className="w-10 h-10 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center text-[#143E78]">
                          <Upload size={18} />
                        </div>
                        <p className="text-xs font-black text-slate-700 mt-1">
                          Click to upload or drag & drop reports
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          PDF, PNG, JPG (Up to 50MB each)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Attached Medical Reports List */}
                  {medicalReports.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {medicalReports.map((report, idx) => {
                        const isPDF = report.url?.toLowerCase().endsWith('.pdf') || report.name?.toLowerCase().endsWith('.pdf');
                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2.5 bg-slate-50 hover:bg-blue-50/50 rounded-2xl border border-slate-200/80 transition-all group"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-white shrink-0 ${isPDF ? 'bg-rose-500' : 'bg-indigo-500'
                                }`}>
                                {isPDF ? <FileText size={15} /> : <Image size={15} />}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-slate-800 truncate">{report.name || `Medical Report ${idx + 1}`}</p>
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                  {isPDF ? 'PDF Document' : 'Image Scan'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleFilePreview(report)}
                                className="p-1.5 text-[#143E78] hover:text-[#143E78] bg-white hover:bg-blue-100 rounded-xl border border-slate-200 transition-all shadow-sm"
                                title="Preview Document"
                              >
                                <Eye size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setMedicalReports(prev => prev.filter((_, i) => i !== idx))}
                                className="p-1.5 text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 rounded-xl border border-rose-200 transition-all shadow-sm"
                                title="Remove Document"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50/70 rounded-2xl border border-dashed border-slate-200 text-center">
                      <Paperclip size={20} className="mx-auto text-slate-300 mb-1" />
                      <p className="text-[11px] font-bold text-slate-400">No medical reports attached yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-6">
            {renderPremiumCardHeader('Schedule Appointment', 'Select date and time slot', <Calendar size={24} />)}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                  <Calendar size={14} className="text-[#143E78]" />
                  Appointment Date <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    ref={scheduleDateRef}
                    min={new Date().toISOString().split('T')[0]}
                    value={formData.appointmentDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, appointmentDate: val, appointmentTimeSlot: '' }));
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={formatDateDisplay(formData.appointmentDate)}
                      onChange={(e) => {
                        const formatted = formatWithHyphens(e.target.value);
                        const parsed = parseDateInput(formatted);
                        setFormData(prev => ({ ...prev, appointmentDate: parsed || '', appointmentTimeSlot: '' }));
                      }}
                      placeholder="DD-MM-YYYY"
                      className={`w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm transition-all outline-none ${
                        isPastDate(formData.appointmentDate)
                          ? 'bg-rose-50/50 border-rose-400 focus:border-rose-500 text-rose-900'
                          : 'bg-white border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10'
                      }`}
                    />
                    <Calendar size={16} className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none ${isPastDate(formData.appointmentDate) ? 'text-rose-500' : 'text-slate-400'}`} />
                  </div>
                </div>
                {isPastDate(formData.appointmentDate) && (
                  <p className="text-[11px] font-bold text-rose-600 flex items-center gap-1.5 mt-1 bg-rose-50 border border-rose-200 rounded-xl px-3 py-1.5 animate-in fade-in">
                    <AlertCircle size={13} className="shrink-0 text-rose-500" />
                    Selected date ({formatDateDisplay(formData.appointmentDate)}) has already passed. Please select today or an upcoming date.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    <Clock size={14} className="text-[#143E78]" />
                    Appointment Time <span className="text-rose-500">*</span>
                  </label>
                  {formData.appointmentTimeSlot && (
                    <span className="px-2.5 py-0.5 bg-[#143E78]/10 text-[#143E78] rounded-lg text-[11px] font-black tracking-wide font-mono">
                      {formatTime12Hour(formData.appointmentTimeSlot)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="time"
                      value={formData.appointmentTimeSlot}
                      onChange={(e) => setFormData(prev => ({ ...prev, appointmentTimeSlot: e.target.value }))}
                      className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                    />
                  </div>
                  <div className="flex items-center p-1 bg-slate-100/90 rounded-2xl border-2 border-slate-200 shrink-0 h-[48px] shadow-2xs">
                    <button
                      type="button"
                      onClick={() => {
                        const current = getTimeParts(formData.appointmentTimeSlot);
                        let newH = current.hour24;
                        if (current.hour24 >= 12) newH = current.hour24 - 12;
                        const newTime = `${String(newH).padStart(2, '0')}:${current.minute}`;
                        setFormData(prev => ({ ...prev, appointmentTimeSlot: newTime }));
                      }}
                      className={`px-3.5 h-full rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                        getTimeParts(formData.appointmentTimeSlot).period === 'AM'
                          ? 'bg-[#143E78] text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                      }`}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const current = getTimeParts(formData.appointmentTimeSlot);
                        let newH = current.hour24;
                        if (current.hour24 < 12) newH = current.hour24 + 12;
                        const newTime = `${String(newH).padStart(2, '0')}:${current.minute}`;
                        setFormData(prev => ({ ...prev, appointmentTimeSlot: newTime }));
                      }}
                      className={`px-3.5 h-full rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                        getTimeParts(formData.appointmentTimeSlot).period === 'PM'
                          ? 'bg-[#143E78] text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                      }`}
                    >
                      PM
                    </button>
                  </div>
                </div>
                {formData.appointmentDoctorId && formData.appointmentDate && (() => {
                  const selectedDateObj = new Date(formData.appointmentDate + 'T00:00:00');
                  const dayName = selectedDateObj.toLocaleDateString('en-US', { weekday: 'long' });
                  const doctorForecast = forecast.find(f => f.doctorId === formData.appointmentDoctorId);
                  const daySchedule = doctorForecast?.weeklySchedule?.find(day => day.day === dayName);
                  const isAvailable = daySchedule?.isAvailable === true;
                  return isAvailable ? (
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Doctor available on selected date</p>
                  ) : null;
                })()}
              </div>
            </div>
            {(formData.patientName || preselectedLead) && (
              <div className="p-5 bg-[#143E78]/5 rounded-2xl border-2 border-[#143E78]/20 space-y-4">
                <p className="text-[10px] font-black text-[#143E78] uppercase tracking-widest flex items-center gap-2">
                  <ShieldCheck size={14} /> Patient Details
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Patient Name</p>
                    <p className="text-sm font-black text-slate-900 mt-0.5">{formData.patientName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone</p>
                    <p className="text-sm font-black text-slate-900 mt-0.5">{formData.patientPhone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email</p>
                    <input
                      type="email"
                      value={formData.patientEmail || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, patientEmail: e.target.value }))}
                      placeholder="Enter patient email"
                      className="w-full mt-0.5 px-3 py-2 bg-white border-2 border-[#143E78]/20 rounded-xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'doctor_type':
        return (
          <div className="space-y-6">
            {renderPremiumCardHeader('Doctor Assignment', 'Choose consultation type and doctor', <Stethoscope size={24} />)}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => { setDoctorType('in_house'); setFormData(prev => ({ ...prev, appointmentDoctorId: '' })); setSelectedCouponId(''); }}
                className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 ${doctorType === 'in_house'
                  ? 'bg-emerald-50 border-emerald-500 shadow-lg shadow-emerald-100'
                  : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
              >
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 ${doctorType === 'in_house' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                  <ShieldCheck size={20} />
                </div>
                <p className="text-sm font-black text-slate-900 uppercase tracking-tight">In-House Doctor</p>
                <p className="text-xs font-bold text-slate-500 mt-1">Free consultation. No payment required.</p>
                {doctorType === 'in_house' && (
                  <span className="inline-flex items-center gap-1 mt-3 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-wider">
                    <CheckCircle size={12} /> Selected
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setDoctorType('coupon'); setFormData(prev => ({ ...prev, appointmentDoctorId: '' })); setSelectedCouponId(''); }}
                className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 ${doctorType === 'coupon'
                  ? 'bg-amber-50 border-amber-500 shadow-lg shadow-amber-100'
                  : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
              >
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 ${doctorType === 'coupon' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                  <Tag size={20} />
                </div>
                <p className="text-sm font-black text-slate-900 uppercase tracking-tight">Empanelment</p>
                <p className="text-xs font-bold text-slate-500 mt-1">Paid consultation. Payment link will be generated.</p>
                {doctorType === 'coupon' && (
                  <span className="inline-flex items-center gap-1 mt-3 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-lg text-[10px] font-black uppercase tracking-wider">
                    <CheckCircle size={12} /> Selected
                  </span>
                )}
              </button>
            </div>

            {doctorType === 'coupon' && ['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
              <div className="p-5 bg-amber-50 rounded-2xl border-2 border-amber-200 space-y-3">
                <label className="flex items-center gap-2 text-[11px] font-black text-amber-700 uppercase tracking-widest">
                  <Tag size={14} /> Select Coupon <span className="text-slate-400 font-bold">(Optional)</span>
                </label>
                <div className="relative">
                  <select
                    value={selectedCouponId}
                    onChange={(e) => {
                      const coupon = coupons.find(p => p._id === e.target.value);
                      setSelectedCouponId(e.target.value);
                      if (coupon) {
                        const taxType = (coupon.taxType && coupon.taxType !== 'none') ? coupon.taxType : 'cgst_sgst';
                        const taxPct = (coupon.taxPercentage !== undefined && coupon.taxPercentage !== null && coupon.taxPercentage > 0) ? coupon.taxPercentage : 18;
                        const pricingResult = calculateFromTotal(
                          pricing.finalPrice,
                          pricing.platformFee,
                          pricing.otherCharges || 0,
                          coupon.discountPercentage || 0,
                          taxType,
                          taxPct
                        );
                        setPricing(prev => ({
                          ...prev,
                          ...pricingResult,
                          discountPercentage: coupon.discountPercentage || 0,
                          editing: true,
                          taxType,
                          taxPercentage: taxPct,
                          taxName: coupon.taxName || ''
                        }));
                        setCouponOriginal({
                          discountPercentage: coupon.discountPercentage || 0,
                          taxType,
                          taxPercentage: taxPct,
                          taxName: coupon.taxName || ''
                        });
                      } else {
                        const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges || 0, 0, 'cgst_sgst', 18);
                        setPricing(prev => ({
                          ...prev,
                          ...pricingResult,
                          discountPercentage: 0,
                          editing: true,
                          taxType: 'cgst_sgst',
                          taxPercentage: 18,
                          taxName: ''
                        }));
                        setCouponOriginal(null);
                      }
                    }}

                    className="w-full px-4 py-3.5 bg-white border-2 border-amber-200 rounded-2xl font-bold text-sm focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 transition-all outline-none appearance-none"
                  >
                    <option value="">-- Select coupon --</option>
                    {coupons.map(coupon => (
                      <option key={coupon._id} value={coupon._id}>
                        {coupon?.name} {coupon.discountPercentage > 0 ? `(Save ${coupon.discountPercentage}%)` : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-amber-400 pointer-events-none" />
                </div>
                {coupons.length === 0 && (
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">No coupons available.</p>
                )}

                {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                  <div className="mt-3 p-4 bg-white rounded-2xl border border-amber-100 space-y-3">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Coupon Pricing & Tax</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Patient Invoice (₹)</label>
                        <input
                          type="number"
                          min="0"
                          value={pricing.finalPrice}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const pricingResult = calculateFromTotal(val, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage);
                            setPricing(prev => ({ ...prev, ...pricingResult }));
                          }}
                          className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl font-bold text-sm focus:border-amber-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Platform Charges (₹)</label>
                        <input
                          type="number"
                          min="0"
                          value={pricing.platformFee}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const pricingResult = calculateFromTotal(pricing.finalPrice, val, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage);
                            setPricing(prev => ({ ...prev, ...pricingResult }));
                          }}
                          className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl font-bold text-sm focus:border-amber-500 outline-none"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Consultation Fee (₹)</label>
                        <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-sm text-slate-700">
                          {formatCurrency(pricing.doctorFee)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Discount (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={pricing.discountPercentage}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, val, pricing.taxType, pricing.taxPercentage);
                            setPricing(prev => ({ ...prev, discountPercentage: val, ...pricingResult }));
                          }}
                          className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl font-bold text-sm focus:border-amber-500 outline-none"
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center justify-between bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-2.5">
                          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">SUB TOTAL (₹)</span>
                          <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.netAmount || 0)}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tax Type</label>
                        <select
                          value={pricing.taxType}
                          onChange={(e) => {
                            const newTaxType = e.target.value;
                            const newTaxPct = (newTaxType === 'cgst_sgst' || newTaxType === 'gst') ? 18 : (newTaxType === 'none' ? 0 : pricing.taxPercentage);
                            const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, newTaxType, newTaxPct);
                            setPricing(prev => ({ ...prev, taxType: newTaxType, taxPercentage: newTaxPct, ...pricingResult }));
                          }}
                          className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl font-bold text-sm focus:border-amber-500 outline-none appearance-none"
                        >
                          <option value="none">None</option>
                          <option value="gst">GST</option>
                          <option value="cgst_sgst">CGST + SGST</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tax Percentage (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={pricing.taxPercentage}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, val);
                            setPricing(prev => ({ ...prev, taxPercentage: val, ...pricingResult }));
                          }}
                          className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl font-bold text-sm focus:border-amber-500 outline-none"
                        />
                      </div>
                      {pricing.taxType === 'custom' && (
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tax Name</label>
                          <input
                            type="text"
                            value={pricing.taxName}
                            onChange={(e) => setPricing(prev => ({ ...prev, taxName: e.target.value }))}
                            className="w-full px-3 py-2 bg-white border border-amber-200 rounded-xl font-bold text-sm focus:border-amber-500 outline-none"
                          />
                        </div>
                      )}
                    </div>
                    {(() => {
                      const taxPercentage = Number(pricing.taxPercentage || 0);
                      const taxType = pricing.taxType || 'none';
                      const pf = Number(pricing.platformFee || 0);
                      const other = Number(pricing.otherCharges || 0);
                      const discountAmount = pf > 0 && pricing.discountPercentage > 0 ? Math.round((pf * pricing.discountPercentage) / 100) : 0;
                      const taxAmount = pricing.taxAmount || 0;
                      const calculatedFinal = pricing.finalPrice || 0;
                      const netAmount = pricing.netAmount || 0;
                      return (
                        <div className="pt-3 border-t border-amber-100 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Consultation Fee</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.doctorFee)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Platform Charges</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pf)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Other Charges</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(other)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">SUB TOTAL</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(netAmount)}</span>
                          </div>
                          {discountAmount > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Discount</span>
                              <span className="text-sm font-black text-emerald-600">-{formatCurrency(discountAmount)}</span>
                            </div>
                          )}
                          {taxAmount > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{taxType === 'cgst_sgst' ? `CGST + SGST @ ${taxPercentage}%` : `GST @ ${taxPercentage}%`}</span>
                              <span className="text-sm font-black text-amber-600">{formatCurrency(taxAmount)}</span>
                            </div>
                          )}
                          <div className="pt-3 border-t-2 border-dashed border-amber-200 flex items-center justify-between">
                            <span className="text-sm font-black text-slate-600 uppercase tracking-wider">Total Payable</span>
                            <span className="text-2xl font-black text-[#143E78]">{formatCurrency(calculatedFinal)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {doctorType === 'coupon' && ['admin', 'customer_care', 'cc_head'].includes(user?.role) && !selectedCouponId && (
              <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
                <p className="text-xs font-bold text-blue-700">No coupon selected. Financials will use default settings. You can select a coupon later to apply discount.</p>
              </div>
            )}

            {doctorType === 'coupon' && !['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
              <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
                <p className="text-xs font-bold text-blue-700">Empanelment consultation. Payment link amount will be ₹500 by default unless updated by admin.</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Sparkles size={14} className="text-amber-500" />
                Consultation Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {APPOINTMENT_TYPES.map(type => {
                  const isSelected = formData.appointmentType === type.value;
                  const colorClass = APPOINTMENT_TYPE_COLORS[type.value] || 'bg-slate-50 text-slate-700 border-slate-200';
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, appointmentType: type.value }))}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${isSelected
                        ? `${colorClass} border-current shadow-md`
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                        }`}
                    >
                      <p className="text-sm font-black uppercase tracking-tight">{type.label}</p>
                      <p className="text-[10px] font-bold text-slate-500 mt-1">{type.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Sparkles size={14} className="text-indigo-500" />
                Mode of Communication
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {COMMUNICATION_MODES.map(mode => {
                  const isSelected = formData.communicationMode === mode.value;
                  const colorClass = COMMUNICATION_MODE_COLORS[mode.value] || 'bg-slate-50 text-slate-700 border-slate-200';
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, communicationMode: mode.value }))}
                      className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${isSelected
                        ? `${colorClass} border-current shadow-md`
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                        }`}
                    >
                      <p className="text-sm font-black uppercase tracking-tight">{mode.label}</p>
                      <p className="text-[10px] font-bold text-slate-500 mt-1">{mode.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Doctor Type Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Stethoscope size={14} className="text-[#143E78]" />
                Doctor Type Filter
              </label>
              <div className="relative">
                <select
                  value={availabilityDoctorType}
                  onChange={(e) => setAvailabilityDoctorType(e.target.value)}
                  className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none cursor-pointer"
                >
                  <option value="">All Doctor Types (Show All Doctors)</option>
                  <option value="Surgeon">Surgeon</option>
                  <option value="Anesthesiologist">Anesthesiologist</option>
                  <option value="Specialist">Specialist</option>
                </select>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {loadingForecast && (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={20} className="animate-spin text-[#143E78]" />
                <span className="ml-2 text-xs font-bold text-slate-500">Checking availability...</span>
              </div>
            )}

            {!loadingForecast && (
              <div className="p-4 bg-[#143E78]/5 rounded-2xl border-2 border-[#143E78]/10">
                <p className="text-[10px] font-black text-[#143E78] uppercase tracking-widest mb-2">
                  Available Doctors: {doctors.length}
                </p>
                <p className="text-xs font-bold text-slate-600">
                  Showing all registered doctors in the system
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Stethoscope size={14} className="text-[#143E78]" />
                Doctor <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.appointmentDoctorId}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointmentDoctorId: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
                >
                  <option value="">-- Select doctor --</option>
                  {doctors.map(doc => {
                    const docId = doc._id || doc.id;
                    const firstName = doc.user?.firstName || doc.firstName || doc.name?.split(' ')[0] || '';
                    const lastName = doc.user?.lastName || doc.lastName || doc.name?.split(' ').slice(1).join(' ') || '';
                    const docName = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (doc.user?.name || doc.name || doc.doctorName || 'Doctor');
                    const phone = doc.user?.phone || doc.phone || doc.contactNumber || '';
                    const spec = doc.specialization || doc.doctorType || doc.user?.specialization || 'General';
                    const fee = doc.financials?.consultationFee || doc.consultationFee;

                    return (
                      <option key={docId} value={docId}>
                        Dr. {docName} {phone ? `| ${phone}` : ''} | {spec} {fee ? `| ₹${fee}` : ''}
                      </option>
                    );
                  })}
                </select>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              {formData.appointmentDoctorId && doctorConsultationFee > 0 && (
                <div className="p-3 bg-[#143E78]/5 rounded-xl border border-[#143E78]/10 flex items-center justify-between">
                  <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest">Consultation Fee</span>
                  <span className="text-sm font-black text-[#143E78]">{formatCurrency(doctorConsultationFee)}</span>
                </div>
              )}
              {doctors.length === 0 && (
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">No doctors available.</p>
              )}
            </div>
          </div>
        );

      case 'payment':
        if (pricing.finalPrice === 0) {
          return (
            <div className="space-y-6">
              {renderPremiumCardHeader('Payment', 'No payment required', <CreditCard size={24} />)}
              <div className="bg-emerald-50 rounded-2xl border-2 border-emerald-200 p-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-emerald-800">Zero-price booking</p>
                    <p className="text-xs font-bold text-emerald-600">Payment is not required. You can proceed directly to confirmation.</p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        if (doctorType === 'coupon') {
          return (
            <div className="space-y-6">
              {renderPremiumCardHeader('Payment Details', 'Generate payment link for consultation', <CreditCard size={24} />)}
              {(selectedCouponId ? (
                <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 space-y-4">
                  {(() => {
                    const coupon = coupons.find(p => p._id === selectedCouponId);
                    if (!coupon) return null;
                    const hasTax = coupon?.taxType && coupon?.taxType !== 'none' && coupon?.taxPercentage > 0;
                    const taxLabel = coupon?.taxType === 'cgst_sgst' ? 'GST' : (coupon?.taxName || 'Tax');
                    const discountAmount = pricing.platformFee > 0 && pricing.discountPercentage > 0 ? Math.round((pricing.platformFee * pricing.discountPercentage) / 100) : 0;
                    return (
                      <>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Package</p>
                          <p className="text-base font-black text-slate-900 mt-1">
                            {coupon?.name}
                          </p>
                        </div>
                        <div className="space-y-3 pt-4 border-t border-slate-100">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Consultation Fee</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.doctorFee)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Platform Charges</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.platformFee)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Other Charges</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.otherCharges)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">SUB TOTAL</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.netAmount || 0)}</span>
                          </div>
                          {discountAmount > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Discount</span>
                              <span className="text-sm font-black text-emerald-600">-{formatCurrency(discountAmount)}</span>
                            </div>
                          )}
                          {hasTax && (
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{taxLabel} @ {coupon?.taxPercentage}%</span>
                              <span className="text-sm font-black text-amber-600">{formatCurrency(pricing.taxAmount || 0)}</span>
                            </div>
                          )}
                          <div className="pt-4 border-t-2 border-dashed border-slate-200 flex items-center justify-between">
                            <span className="text-sm font-black text-slate-600 uppercase tracking-wider">Total Payable</span>
                            <span className="text-2xl font-black text-[#143E78]">
                              {formatCurrency(pricing.finalPrice)}
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 space-y-4">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Payment Summary</p>
                    <p className="text-base font-black text-slate-900 mt-1">Consultation pricing breakdown</p>
                  </div>
                  <div className="space-y-3 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Consultation Fee</span>
                      <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.doctorFee || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Platform Charges</span>
                      <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.platformFee || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Other Charges</span>
                      <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.otherCharges || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">SUB TOTAL</span>
                      <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.netAmount || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{pricing.taxType === 'cgst_sgst' ? `CGST + SGST @ ${pricing.taxPercentage || 18}%` : `GST @ ${pricing.taxPercentage || 18}%`}</span>
                      <span className="text-sm font-black text-amber-600">{formatCurrency(pricing.taxAmount || 0)}</span>
                    </div>
                    {(pricing.platformFee > 0 && pricing.discountPercentage > 0) && (
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Discount</span>
                        <span className="text-sm font-black text-emerald-600">-{formatCurrency(Math.round((pricing.platformFee * pricing.discountPercentage) / 100))}</span>
                      </div>
                    )}
                    <div className="pt-4 border-t-2 border-dashed border-slate-200 flex items-center justify-between">
                      <span className="text-sm font-black text-slate-600 uppercase tracking-wider">Total Payable</span>
                      <span className="text-2xl font-black text-[#143E78]">{formatCurrency(pricing.finalPrice || 0)}</span>
                    </div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Select a coupon to apply discount and tax settings.</p>
                  </div>
                </div>
              ))}
              {paymentLink ? (
                <div className="bg-emerald-50 rounded-2xl border-2 border-emerald-200 p-5 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-xl">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-emerald-800">Payment Link Generated</p>
                      <p className="text-xs font-bold text-emerald-600">Share with patient to complete payment</p>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-emerald-100">
                    <a
                      href={paymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#143E78] underline break-all hover:text-blue-700 transition-colors"
                    >
                      {paymentLink}
                    </a>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(paymentLink);
                        toast.success('Payment link copied to clipboard');
                      }}
                      className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-200"
                    >
                      <CreditCard size={16} /> Copy Payment Link
                    </button>
                    {isEditMode && ['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                      <button
                        type="button"
                        onClick={async () => {
                          setPaymentLink('');
                          await generatePaymentLink();
                        }}
                        className="flex-1 px-4 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#143E78]/20"
                      >
                        {generatingPayment ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                        {generatingPayment ? 'Generating...' : 'Regenerate Link'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={generatePaymentLink}
                  disabled={generatingPayment}
                  className="w-full px-6 py-4 bg-[#143E78] text-white rounded-2xl text-sm font-black uppercase tracking-wider hover:bg-[#0f2f5e] disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg shadow-[#143E78]/20 transition-all"
                >
                  {generatingPayment ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {isEditMode ? 'Generate New Payment Link' : 'Generate Payment Link'}
                </button>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-6">
            {renderPremiumCardHeader('Review & Confirm', 'Verify appointment details', <ClipboardCheck size={24} />)}
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 space-y-4">
              {[
                { label: 'Patient Name', value: formData.patientName },
                { label: 'Phone', value: formData.patientPhone },
                { label: 'Email', value: formData.patientEmail || 'Not provided' },
                { label: 'Status', value: isEditMode ? formData.status?.replace(/_/g, ' ') : 'Draft', capitalize: true },
                { label: 'Consultation Type', value: formData.appointmentType === 'consultation' ? 'Online Consultation' : formData.appointmentType?.replace(/_/g, ' '), capitalize: true },
                { label: 'Communication Mode', value: formData.communicationMode?.replace(/_/g, ' '), capitalize: true },
                { label: 'Date', value: formData.appointmentDate ? formatDateDisplay(formData.appointmentDate) : 'N/A' },
                { label: 'Time', value: formData.appointmentTimeSlot ? formatTime12Hour(formData.appointmentTimeSlot) : 'N/A' },
                { label: 'Doctor', value: getDoctorDisplayName(doctors.find(d => (d._id || d.id) === formData.appointmentDoctorId)) || 'Not selected' },
              ].map((field, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{field.label}</span>
                  <span className={`text-sm font-black text-slate-900 capitalize ${field.capitalize ? 'capitalize' : ''}`}>
                    {field.value}
                  </span>
                </div>
              ))}
              {doctorType === 'coupon' && selectedCouponId && (
                <>
                  <div className="flex items-center justify-between py-2 border-t-2 border-dashed border-slate-200 mt-4 pt-4">
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Package</span>
                    <span className="text-sm font-black text-slate-900">
                      {coupons.find(p => p._id === selectedCouponId)?.name}
                    </span>
                  </div>
                  {pricing.editing && (
                    <>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Consultation Fee</span>
                        <span className="text-sm font-black text-slate-500 line-through">{formatCurrency(pricing.doctorFee)}</span>
                      </div>
                      {pricing.platformFee > 0 && (
                        <div className="flex items-center justify-between py-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Platform Charges</span>
                          <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.platformFee)}</span>
                        </div>
                      )}
                      {pricing.platformFee > 0 && pricing.discountPercentage > 0 && (
                        <div className="flex items-center justify-between py-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Discount on Platform</span>
                          <span className="text-sm font-black text-emerald-600">
                            -{formatCurrency(Math.round((pricing.platformFee * pricing.discountPercentage) / 100))}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between py-2">
                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Total Patient Invoice</span>
                        <span className="text-xl font-black text-[#143E78]">{formatCurrency(pricing.finalPrice)}</span>
                      </div>
                    </>
                  )}
                </>
              )}
              {doctorType === 'coupon' && selectedCouponId && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Payment</span>
                  <span className="text-sm font-black text-emerald-600 flex items-center gap-1">
                    {paymentLink ? <><CheckCircle size={14} /> Link Generated</> : 'Not generated'}
                  </span>
                </div>
              )}
            </div>
            {appointmentId && (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-2xl border-2 border-emerald-200">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-sm font-black text-emerald-800">
                  {isEditMode ? 'Review changes and save.' : 'Appointment draft created. Click confirm to finalize.'}
                </p>
              </div>
            )}
          </div>
        );

      case 'confirm':
        return (
          <div className="space-y-6">
            {renderPremiumCardHeader('Review & Confirm', 'Final verification', <ClipboardCheck size={24} />)}
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 space-y-4">
              {[
                { label: 'Patient Name', value: formData.patientName },
                { label: 'Phone', value: formData.patientPhone },
                { label: 'Email', value: formData.patientEmail || 'Not provided' },
                { label: 'Status', value: isEditMode ? formData.status?.replace(/_/g, ' ') : 'Draft', capitalize: true },
                { label: 'Consultation Type', value: formData.appointmentType === 'consultation' ? 'Online Consultation' : formData.appointmentType?.replace(/_/g, ' '), capitalize: true },
                { label: 'Communication Mode', value: formData.communicationMode?.replace(/_/g, ' '), capitalize: true },
                { label: 'Date', value: formData.appointmentDate ? formatDateDisplay(formData.appointmentDate) : 'N/A' },
                { label: 'Time', value: formData.appointmentTimeSlot ? formatTime12Hour(formData.appointmentTimeSlot) : 'N/A' },
                { label: 'Doctor', value: getDoctorDisplayName(doctors.find(d => (d._id || d.id) === formData.appointmentDoctorId)) || 'Not selected' },
              ].map((field, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{field.label}</span>
                  <span className={`text-sm font-black text-slate-900 capitalize ${field.capitalize ? 'capitalize' : ''}`}>
                    {field.value}
                  </span>
                </div>
              ))}
              {doctorType === 'coupon' && selectedCouponId && (
                <>
                  <div className="flex items-center justify-between py-2 border-t-2 border-dashed border-slate-200 mt-4 pt-4">
                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Coupon</span>
                    <span className="text-sm font-black text-slate-900">
                      {coupons.find(p => p._id === selectedCouponId)?.name}
                    </span>
                  </div>
                  {(() => {
                    const coupon = coupons.find(p => p._id === selectedCouponId);
                    const hasTax = coupon?.taxType && coupon?.taxType !== 'none' && coupon?.taxPercentage > 0;
                    const taxLabel = coupon?.taxType === 'cgst_sgst' ? 'GST' : (coupon?.taxName || 'Tax');
                    const discountAmount = pricing.platformFee > 0 && pricing.discountPercentage > 0 ? Math.round((pricing.platformFee * pricing.discountPercentage) / 100) : 0;
                    return (
                      <>
                        <div className="flex items-center justify-between py-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Consultation Fee</span>
                          <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.doctorFee)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Platform Charges</span>
                          <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.platformFee)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Other Charges</span>
                          <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.otherCharges)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">SUB TOTAL</span>
                          <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.netAmount || 0)}</span>
                        </div>
                        {discountAmount > 0 && (
                          <div className="flex items-center justify-between py-2">
                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Discount</span>
                            <span className="text-sm font-black text-emerald-600">-{formatCurrency(discountAmount)}</span>
                          </div>
                        )}
                        {hasTax && (
                          <div className="flex items-center justify-between py-2">
                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{taxLabel} @ {coupon?.taxPercentage}%</span>
                            <span className="text-sm font-black text-amber-600">{formatCurrency(pricing.taxAmount || 0)}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between py-2 border-t-2 border-dashed border-slate-200 mt-2 pt-2">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Total Payable</span>
                          <span className="text-xl font-black text-[#143E78]">{formatCurrency(pricing.finalPrice)}</span>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
              {doctorType === 'coupon' && selectedCouponId && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Payment</span>
                  <span className="text-sm font-black text-emerald-600 flex items-center gap-1">
                    {paymentLink ? <><CheckCircle size={14} /> Link Generated</> : 'Not generated'}
                  </span>
                </div>
              )}
            </div>
            {appointmentId && (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-2xl border-2 border-emerald-200">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-sm font-black text-emerald-800">
                  {isEditMode ? 'Review changes and save.' : 'Appointment draft created. Click confirm to finalize.'}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (fetchingOptions) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Loader2 size={40} className="animate-spin text-[#143E78]" />
        <p className="text-xs font-bold text-slate-500">Loading doctors & coupons...</p>
        <button
          type="button"
          onClick={() => setFetchingOptions(false)}
          className="text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-rose-700"
        >
          Skip & Continue
        </button>
      </div>
    );
  }


  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-10">
      {/* Premium Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/appointments')}
          className="p-3 bg-white hover:bg-slate-50 rounded-2xl transition-all border border-slate-200 shadow-sm group"
        >
          <ArrowLeft size={20} className="text-slate-600 group-hover:text-[#143E78] transition-colors" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{isEditMode ? 'Edit Appointment' : 'New Appointment'}</h1>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-0.5">
            {isEditMode ? 'Update appointment details' : 'Book a pre-surgery consultation for an existing lead'}
          </p>
        </div>
      </div>

      {/* Premium Stepper */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8">
        {renderPremiumStepper()}

        <div className="min-h-[400px] mt-10">
          {renderStepContent()}
        </div>

        <div className="flex gap-4 pt-8 border-t-2 border-slate-100 mt-8">
          <button
            type="button"
            onClick={() => {
              if (currentStep === 0) {
                clearDraft();
                if (isEditMode && appointmentId) {
                  navigate(`/appointments/${appointmentId}`);
                } else {
                  navigate('/appointments');
                }
              } else {
                const prevIdx = currentStep - 1;
                const activeLeadId = formData.leadId || preselectedLead?._id || searchParams.get('leadId') || searchParams.get('fromLeadId');
                if (activeLeadId && visibleSteps[prevIdx]?.key) {
                  localStorage.setItem(`doxez_lead_last_step_${activeLeadId}`, visibleSteps[prevIdx].key);
                }
                setCurrentStep(prevIdx);
                updateUrlStep(prevIdx);
              }
            }}
            disabled={currentStep === 0 && !preselectedLead}
            className="flex-1 px-6 py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center gap-2 transition-all"
          >
            <ChevronLeft size={18} />
            <span className="text-xs font-black uppercase tracking-wider">{currentStep === 0 ? (isEditMode ? 'Cancel' : 'Cancel') : 'Back'}</span>
          </button>
          {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (appointmentId || formData.leadId) && (
            <button
              type="button"
              onClick={() => setShowLostModal(true)}
              className="px-6 py-4 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg shadow-rose-200"
            >
              <XCircle size={18} />
              <span className="text-xs font-black uppercase tracking-wider">Mark as Lost</span>
            </button>
          )}
          {currentStep < visibleSteps.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !canProceedFromStep(visibleSteps[currentStep]?.key)}
              className="flex-1 px-6 py-4 bg-[#143E78] text-white rounded-2xl hover:bg-[#0f2f5e] disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#143E78]/20"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : (
                <>
                  <span className="text-xs font-black uppercase tracking-wider">Next</span>
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          ) : isEditMode ? (
            <button
              type="button"
              onClick={handleConfirmAppointment}
              disabled={loading}
              className="flex-1 px-6 py-4 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-200"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
              <span className="text-xs font-black uppercase tracking-wider">Save Changes</span>
            </button>
          ) : (doctorType === 'coupon' && paymentLink) ? (
            <button
              type="button"
              disabled={true}
              title="Payment is pending. Complete the payment before confirming the appointment."
              className="flex-1 px-6 py-4 bg-amber-600 text-white rounded-2xl opacity-60 cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-lg shadow-amber-200"
            >
              <span className="text-xs font-black uppercase tracking-wider">Payment Pending</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirmAppointment}
              disabled={loading}
              className="flex-1 px-6 py-4 bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-200"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
              <span className="text-xs font-black uppercase tracking-wider">Confirm Appointment</span>
            </button>
          )}
        </div>

        {/* Mark as Lost Modal */}
        {showLostModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
            <div className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_20px_80px_-20px_rgba(20,62,120,0.25)] w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-black text-rose-600 tracking-tight">Mark Appointment as Lost</h3>
                <button onClick={() => setShowLostModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-slate-600 font-medium leading-relaxed mb-4">Please provide a reason for marking this appointment as lost. This action will also update the lead status.</p>
              <div className="space-y-2 mb-6">
                <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                  Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  placeholder="Enter reason for marking as lost..."
                  rows={3}
                  className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 transition-all outline-none resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowLostModal(false)} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">
                  Dismiss
                </button>
                <button onClick={handleMarkAsLost} disabled={submittingLost} className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-2xl text-xs font-black hover:bg-rose-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  {submittingLost ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle size={16} />}
                  {submittingLost ? 'Updating...' : 'Mark as Lost'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Preview Modal */}
        {previewFile && createPortal(
          <div onClick={() => setPreviewFile(null)} className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[100000] flex items-center justify-center p-4 overflow-y-auto">
            <div className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_20px_80px_-20px_rgba(20,62,120,0.35)] w-full max-w-4xl p-6 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded-xl">
                    {previewFile.isPDF ? <FileText className="w-5 h-5 text-indigo-600" /> : <Image className="w-5 h-5 text-indigo-600" />}
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-800 tracking-tight">Document Preview</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 max-w-[300px] truncate">{previewFile.name}</p>
                  </div>
                </div>
                <button onClick={() => setPreviewFile(null)} className="p-3 bg-slate-100 hover:bg-red-50 hover:text-red-500 rounded-2xl transition-all group">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="rounded-2xl border-2 border-slate-100 overflow-hidden bg-slate-50">
                {previewFile.isPDF ? (
                  <iframe src={`${previewFile.url}#toolbar=0`} className="w-full h-[60vh] min-h-[500px] rounded-2xl border-none" title="PDF Preview" />
                ) : (
                  <img src={previewFile.url} alt="Preview" className="max-w-full h-auto rounded-2xl shadow-2xl border-4 border-white mx-auto" />
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <a href={previewFile.url} download={previewFile.name || 'document'} className="px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all flex items-center gap-2">
                  <Download size={14} /> Download
                </a>
              </div>
            </div>
          </div>
          , document.body)}
      </div>
    </div>
  );
};

export default AppointmentCreate;
